import ErrorMessage from '@/components/ErrorMessage';
import { BaseErrorModel, BaseResponseModel } from '@/models/core.model';
import { ManualDeductPointsRequest } from '@/models/point-transaction.model';
import { manualDeductPoints } from '@/services/point-transaction.service';
import { getUsersLookup } from '@/services/user.service';
import { AxiosError } from 'axios';
import React, { useState } from 'react';
import { Drawer, DrawerProps, Button, Form, Schema, FormInstance, NumberInput } from 'rsuite';
import FetchingSelect from '@/components/FetchingSelect';
import { toastSuccessMessage } from '@/utils/toasterMessage';
import { getPointTypesLookup } from '@/services/point-type.service';
import { ELITES_POINT_TYPE_ID } from '@/models/point-type.model';

const modelSchema = Schema.Model<ManualDeductPointsRequest>({
  userId: Schema.Types.NumberType().isRequired('This field is required.'),
  amount: Schema.Types.NumberType().isRequired('This field is required.').min(1, 'Amount must be at least 1.'),
  note: Schema.Types.StringType(),
  idempotencyKey: Schema.Types.StringType(),
});

const emptyModel: ManualDeductPointsRequest = {
  userId: undefined,
  amount: undefined,
  note: undefined,
  idempotencyKey: undefined,
};

interface DeductDrawerProps extends DrawerProps {
  userId?: number;
  onSubmitted?: () => void;
}

const DeductDrawer = (props: DeductDrawerProps) => {
  const { open, onClose, onSubmitted, userId, ...rest } = props;

  const [formValue, setFormValue] = useState<ManualDeductPointsRequest>({ ...emptyModel });
  const [formError, setFormError] = useState({});
  const [errorModel, setErrorModel] = useState<BaseErrorModel | undefined>(undefined);
  const [loading, setLoading] = useState(false);

  const formRef = React.useRef<FormInstance<Record<string, any>>>(null);

  React.useEffect(() => {
    if (open) {
      setFormValue({ ...emptyModel, userId: userId, pointTypeId: ELITES_POINT_TYPE_ID });
    }
  }, [open, userId]);

  const handleClose = (event?: React.SyntheticEvent<Element, Event> | undefined) => {
    setFormValue({ ...emptyModel });
    setErrorModel(undefined);
    setLoading(false);
    if (onClose) {
      onClose(event);
    }
  };

  const handleSubmit = async () => {
    if (formRef.current && !formRef.current.check()) {
      return;
    }
    setErrorModel(undefined);
    setLoading(true);
    try {
      const result: BaseResponseModel<any> = await manualDeductPoints({ ...formValue, pointTypeId: formValue.pointTypeId ?? ELITES_POINT_TYPE_ID });
      if (result.success) {
        toastSuccessMessage('Points deducted successfully');
        handleClose();
        onSubmitted?.();
      }
    } catch (err) {
      setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (value: Record<string, any>) => {
    setFormValue(prev => ({
      ...prev,
      ...value,
    }));
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right"
      open={open}
      onClose={handleClose}
      {...rest}
    >
      <Drawer.Header>
        <Drawer.Title>Deduct Points</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleSubmit} loading={loading} appearance="primary">
            Submit
          </Button>
          <Button onClick={handleClose} appearance="subtle">
            Cancel
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form name='deduct-points-form' fluid
          ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}
          disabled={loading}
        >
          <Form.Group>
            <Form.Label>User</Form.Label>
            <Form.Control name="userId"
              accepter={FetchingSelect}
              fetchData={(text, page) => getUsersLookup(text, undefined, page)}
              placeholder="Select a user"
              block
              disabled={!!userId}
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Point Type</Form.Label>
            <Form.Control name="pointTypeId"
              accepter={FetchingSelect}
              fetchData={(text, page) => getPointTypesLookup(text, page)}
              placeholder="ELITES (default)"
              block
            />
          </Form.Group>
          <Form.Group>
            <Form.Label>Amount</Form.Label>
            <Form.Control name="amount" accepter={NumberInput} min={1} />
          </Form.Group>
          <Form.Group>
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control name="note" />
          </Form.Group>
        </Form>
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom: 20 }} />}
      </Drawer.Body>
    </Drawer>
  );
};

export default DeductDrawer;
